import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import ShareBadge from './ShareBadge';
import { useNavigate } from 'react-router-dom';
import {
  createBadgeClass,
  createBadgeAssertion,
  generateBadgeImage,
  getBadgeForCourse
} from './badgeService';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import { 
  FiShare2, 
  FiDownload, 
  FiAward, 
  FiCalendar, 
  FiCheckCircle, 
  FiAlertCircle,
  FiCheck 
} from 'react-icons/fi';
import { useAuth } from '../../features/auth/contexts/AuthContext';
import toast from '../../components/Toast/Toast';

interface BadgeDisplayProps {
  courseId: string;
  className?: string;
  courseDetails?: {
    is_completed: boolean;
    last_activity_at: string;
    progress_percentage: number;
    completed_tutorial_count: number;
    // Accept either naming convention
    course_title?: string;
    title?: string;
    path_title?: string;
    pathTitle?: string;
  };
}

const CaptureableBadge: React.FC<{
  badgeImage: string;
  courseTitle: string;
  pathTitle: string;
  completedDate: string;
  id: string;
}> = ({ badgeImage, courseTitle, pathTitle, completedDate, id }) => {
  return (
    <div
      id={id}
      style={{
        background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
        borderRadius: '12px',
        padding: '3px',
        width: '240px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          padding: '16px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <img
            src={badgeImage}
            alt={courseTitle}
            style={{
              width: '96px',
              height: '96px',
              margin: '0 auto',
              display: 'block',
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}
          />
        </div>
        <div style={{ textAlign: 'center' }}>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 4px 0',
              background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              textAlign: 'center',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '48px',
              padding: '0 8px'
            }}
          >
            {courseTitle}
          </h3>
          <p
            style={{
              fontSize: '14px',
              margin: '0 0 12px 0',
              color: '#6b7280'
            }}
          >
            {pathTitle}
          </p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'center',
              fontSize: '12px'
            }}
          >
            <div
              style={{
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FiCheckCircle style={{ width: '12px', height: '12px' }} />
              <span>Completed</span>
            </div>
            <div
              style={{
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FiCalendar style={{ width: '12px', height: '12px' }} />
              <span>{completedDate}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BadgeDisplay: React.FC<BadgeDisplayProps> = ({
  courseId,
  className = '',
  courseDetails
}) => {
  const { t } = useTranslation();
  const [badgeImage, setBadgeImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { session } = useAuth();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadBadge = async () => {
      if (!courseDetails?.is_completed || !session?.user) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Get badge data
        const badge = await getBadgeForCourse(courseId, session.user.id);
        if (!badge) {
          throw new Error('Failed to get badge data');
        }

        // Generate badge image
        const imageUrl = await generateBadgeImage(
          badge.name,
          badge.description,
          badge.type
        );
        
        setBadgeImage(imageUrl);
      } catch (err) {
        console.error('Error loading badge:', err);
        setError(err instanceof Error ? err.message : 'Failed to load badge');
      } finally {
        setIsLoading(false);
      }
    };

    loadBadge();
  }, [courseId, courseDetails?.is_completed, session?.user?.id]);

  const handleDownload = async () => {
    if (!badgeImage || !courseDetails) return;
    
    try {
      const captureId = `badge-capture-${courseId}`;
      
      // Create and append the captureable element to a hidden container
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      document.body.appendChild(container);
      
      // Render the captureable badge with modified styles for better capture
      const root = createRoot(container);
      root.render(
        <div style={{ display: 'inline-block' }}> {/* Wrapper to contain the size */}
          <img 
            src={badgeImage}
            alt={courseDetails.title || 'Badge'}
            style={{
              width: '800px', // Larger size for better quality
              height: '800px',
              objectFit: 'contain',
              margin: 0,
              padding: 0,
              display: 'block'
            }}
          />
        </div>
      );
  
      // Wait for image to load
      await new Promise(resolve => setTimeout(resolve, 500));
  
      const element = document.getElementById(captureId) || container.firstChild;
      if (!element) {
        throw new Error('Failed to find badge element');
      }
  
      const canvas = await html2canvas(element, {
        backgroundColor: null, // Transparent background
        scale: 1, // No need to scale since we're already using a large image
        logging: false,
        width: 800,
        height: 800,
        onclone: (clonedDoc) => {
          // Additional cleanup of cloned element if needed
          const clonedElement = clonedDoc.getElementById(captureId);
          if (clonedElement) {
            clonedElement.style.width = '800px';
            clonedElement.style.height = '800px';
          }
        }
      });
  
      // Clean up: unmount and remove the container
      root.unmount();
      document.body.removeChild(container);
  
      // Convert to PNG with transparency
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      
      // Use a sanitized course title for the filename
      const fileName = (
        courseDetails.course_title ||
        courseDetails.title ||
        'badge'
      ).toLowerCase().replace(/[^a-z0-9]+/g, '-');
      
      const link = document.createElement('a');
      link.download = `${fileName}-badge.png`;
      link.href = dataUrl;
      link.click();
      
      toast({
        title: t('badges.downloadSuccess'),
        description: t('badges.downloadSuccessDescription'),
        status: 'success'
      });
    } catch (error) {
      console.error('Error downloading badge:', error);
      toast({
        title: t('badges.errorDownloadingBadge'),
        description: t('badges.errorDownloadingBadgeDescription'),
        status: 'error'
      });
    }
  };

  const handleShare = () => {
    if (!badgeImage) return;
    setIsShareModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="card bg-base-200 shadow-lg animate-pulse min-h-[200px] flex items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  // If there is no badge, course details, or if the course isn't completed, render nothing.
  if (!badgeImage || !courseDetails || !courseDetails.is_completed) {
    return null;
  }

  const formattedDate = courseDetails.last_activity_at
    ? format(new Date(courseDetails.last_activity_at), 'PP')
    : t('badges.completedDate');

  // Use fallback values for the course title and path title if needed.
  const displayCourseTitle = courseDetails.course_title || courseDetails.title || "Untitled Course";
  const displayPathTitle = courseDetails.path_title || courseDetails.pathTitle || "Unknown Path";

  return (
    <div className={`group relative ${className}`}>
      {/* Outer glow effect */}
      <div className="absolute -inset-0.5 bg-primary/20 rounded-box blur-sm opacity-30 group-hover:opacity-70 transition-opacity duration-300"></div>
      
      <div className="card bg-base-200/90 backdrop-blur-sm border border-primary/40 shadow-[0_5px_15px_rgba(0,0,0,0.2)] relative overflow-hidden group-hover:shadow-[0_5px_20px_rgba(138,75,255,0.3)] transition-all duration-300">
        {/* Decorative corner elements */}
        <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-primary/60"></div>
        <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-primary/60"></div>
        
        {/* Decorative circuit line */}
        <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-primary/60 to-transparent"></div>
        
        {/* Scanlines effect */}
        <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(138,75,255,0.05)_50%)] bg-[length:100%_3px] opacity-10"></div>
        
        <div className="card-body p-3 sm:p-5 flex flex-col gap-3 relative z-10">
          {/* Badge Image and Title */}
          <div className="flex flex-col items-center text-center">
            {badgeImage ? (
              <div className="relative group/badge">
                {/* Animated glow effect */}
                <div className="absolute -inset-2 rounded-full bg-primary/20 blur-md opacity-60 group-hover/badge:opacity-100 group-hover/badge:animate-pulse-glow transition-opacity"></div>
                
                <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-full p-0.5 bg-gradient-to-br from-primary/80 via-primary/40 to-transparent rotate-[-45deg] group-hover/badge:rotate-0 transition-transform duration-1000">
                  <img
                    src={badgeImage}
                    alt={courseDetails.title}
                    className="w-full h-full object-contain rounded-full bg-base-200/90 p-1 rotate-45 group-hover/badge:rotate-0 transition-transform duration-1000"
                  />
                </div>
                
                {/* Decorative dots around the badge */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-1 h-1 rounded-full bg-primary/70"></div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1 w-1 h-1 rounded-full bg-primary/70"></div>
                <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-1 h-1 rounded-full bg-primary/70"></div>
                <div className="absolute right-0 top-1/2 translate-x-1 -translate-y-1/2 w-1 h-1 rounded-full bg-primary/70"></div>
              </div>
            ) : (
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-base-300/80 rounded-full flex items-center justify-center relative">
                <div className="absolute inset-0 rounded-full border border-primary/20 animate-pulse"></div>
                <FiAward className="w-10 h-10 text-primary/40" />
              </div>
            )}
            
            <h3 className="mt-3 font-mono font-semibold text-sm sm:text-base text-primary/90 line-clamp-2 group-hover:text-primary transition-colors">
              {courseDetails.title}
            </h3>
          </div>

          {/* Status and Date */}
          <div className="flex flex-col items-center gap-1">
            <div className="badge badge-sm badge-success gap-1 font-medium shadow-sm">
              <FiCheck className="w-3 h-3" />
              {t('profile.completed')}
            </div>
            <span className="text-xs font-mono text-base-content/70">
              {new Date(courseDetails.last_activity_at).toLocaleDateString()}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col xs:flex-row gap-2 w-full mt-1 relative">
            {/* Button glow effect on hover */}
            <div className="absolute -inset-1 opacity-0 group-hover:opacity-100 bg-primary/10 blur-sm rounded-md transition-opacity"></div>
            
            <button
              onClick={handleDownload}
              className="btn btn-xs xs:btn-sm gap-1 flex-1 bg-transparent border border-primary/40 hover:bg-primary/20 hover:border-primary text-primary relative z-10 shadow-[0_2px_10px_rgba(0,0,0,0.1)] hover:shadow-[0_2px_15px_rgba(138,75,255,0.2)]"
              disabled={!badgeImage}
            >
              <FiDownload className="w-3 h-3 xs:w-4 xs:h-4" />
              <span className="whitespace-nowrap text-xs font-mono uppercase tracking-wide">
                {t('profile.download_badge')}
              </span>
            </button>
            
            <button
              onClick={handleShare}
              className="btn btn-xs xs:btn-sm gap-1 flex-1 bg-transparent border border-primary/40 hover:bg-primary/20 hover:border-primary text-primary relative z-10 shadow-[0_2px_10px_rgba(0,0,0,0.1)] hover:shadow-[0_2px_15px_rgba(138,75,255,0.2)]"
              disabled={!badgeImage}
            >
              <FiShare2 className="w-3 h-3 xs:w-4 xs:h-4" />
              <span className="whitespace-nowrap text-xs font-mono uppercase tracking-wide">
                {t('profile.share_badge')}
              </span>
            </button>
          </div>
        </div>
      </div>


      {/* Share Modal */}
      {badgeImage && (
        <ShareBadge
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          onDownload={handleDownload}
          badgeImage={badgeImage}
          badgeName={courseDetails.title}
        />
      )}
    </div>
  );
};

export default BadgeDisplay;